import { type } from 'arktype';

export const ShiftApplicationType = {
  APPLICATION: 'application',
  SWAP: 'swap',
  TAKEOVER: 'takeover',
} as const;
export type ShiftApplicationType = (typeof ShiftApplicationType)[keyof typeof ShiftApplicationType];

export const ShiftApplicationStatus = {
  APPLIED: 'applied',
  WITHDRAWN: 'withdrawn',
  SUPERSEDED: 'superseded',
  PENDING: 'pending',
  PENDING_PLANNER_APPROVAL: 'pending_planner_approval',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
} as const;
export type ShiftApplicationStatus = (typeof ShiftApplicationStatus)[keyof typeof ShiftApplicationStatus];

/**
 * ArkType schema for creating a shift application (employee self-apply).
 */
export const createShiftApplicationSchema = type({
  planInstanceId: type('string').configure({
    description: 'ID of the plan instance containing the target shift unit.',
    examples: ['planinst_abc123'],
  }),
  shiftUnitId: type('string').configure({
    description: 'ID of the shift unit the employee is applying to.',
    examples: ['shiftunit_abc123'],
  }),
  date: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'ISO 8601 calendar date (YYYY-MM-DD) for which the application applies.',
    examples: ['2026-04-22'],
  }),
}).configure({ description: 'Payload for an employee applying to an open shift unit on a specific date.' });

export type CreateShiftApplication = typeof createShiftApplicationSchema.t;

/**
 * ArkType schema for querying open shifts.
 */
export const openShiftsQuerySchema = type({
  from: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'Inclusive start of the date window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  to: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'Inclusive end of the date window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
}).configure({ description: 'Query parameters selecting the date window for open-shift discovery.' });

export type OpenShiftsQuery = typeof openShiftsQuerySchema.t;

// =============================================================================
// Shift Swap & Takeover Schemas
// =============================================================================

/**
 * ArkType schema for marking a shift as available for swap or takeover.
 */
export const markShiftSchema = type({
  planInstanceId: type('string').configure({
    description: 'ID of the plan instance containing the marked shift unit.',
    examples: ['planinst_abc123'],
  }),
  shiftUnitId: type('string').configure({
    description: 'ID of the shift unit being marked.',
    examples: ['shiftunit_abc123'],
  }),
  date: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'ISO 8601 calendar date (YYYY-MM-DD) the marking applies to.',
    examples: ['2026-04-22'],
  }),
  'swapStatus?': type("'looking_for_swap' | 'available_for_takeover'").configure({
    description:
      "Swap intent. 'looking_for_swap' offers the shift in exchange for another; 'available_for_takeover' gives it up with no return.",
    examples: ['looking_for_swap'],
  }),
  'startTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/ | null').configure({
    description: 'Optional employee-owned start-time override for the assigned shift (HH:mm).',
    examples: ['08:30'],
  }),
  'endTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/ | null').configure({
    description: 'Optional employee-owned end-time override for the assigned shift (HH:mm).',
    examples: ['16:30'],
  }),
  'targetPersonId?': type('string').configure({
    description:
      'When an admin or planner overrides the shift time on behalf of another employee, pass the target person ID. Requires calendars:update and plans:update permissions.',
    examples: ['person_abc123'],
  }),
}).configure({
  description:
    'Payload marking an assigned shift unit as available for swap/takeover or updating its employee-owned time. Work location is day-scoped and lives on the personal calendar; pass locationId here is no longer accepted.',
});

export type MarkShift = typeof markShiftSchema.t;

/**
 * ArkType schema for removing a swap/takeover marking from a shift.
 */
export const unmarkShiftSchema = type({
  planInstanceId: type('string').configure({
    description: 'ID of the plan instance containing the shift unit.',
    examples: ['planinst_abc123'],
  }),
  shiftUnitId: type('string').configure({
    description: 'ID of the shift unit whose marking is being removed.',
    examples: ['shiftunit_abc123'],
  }),
  date: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'ISO 8601 calendar date (YYYY-MM-DD) of the marking to clear.',
    examples: ['2026-04-22'],
  }),
}).configure({ description: 'Payload for removing a swap/takeover marking from a shift unit.' });

export type UnmarkShift = typeof unmarkShiftSchema.t;

/**
 * ArkType schema for querying the current employee's assigned shifts
 * (for offering in a swap).
 */
export const swapMyShiftsQuerySchema = type({
  from: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'Inclusive start of the date window (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  to: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'Inclusive end of the date window (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'targetEmployeeId?': type('string').configure({
    description: 'Optional filter: only return shifts compatible with this target employee.',
    examples: ['employee_abc123'],
  }),
  'targetShiftUnitId?': type('string').configure({
    description: 'Optional filter: only return shifts compatible with this target shift unit.',
    examples: ['shiftunit_abc123'],
  }),
  'targetDate?': type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'Optional filter: only return shifts that can be offered for this target date.',
    examples: ['2026-04-22'],
  }),
}).configure({ description: "Query parameters for listing the current employee's shifts eligible to be offered in a swap." });

export type SwapMyShiftsQuery = typeof swapMyShiftsQuerySchema.t;

export const swapRequestsQuerySchema = type({
  'scope?': type("'mine' | 'team-approvals' | 'all'").configure({
    description:
      "Scope filter. 'mine' = requests I sent or received; 'team-approvals' = pending planner approvals in my team; 'all' = everything visible.",
    examples: ['mine'],
  }),
}).configure({ description: 'Query parameters for listing swap/takeover requests.' });

export type SwapRequestsQuery = typeof swapRequestsQuerySchema.t;

/**
 * ArkType schema for creating a swap or takeover request.
 *
 * For swaps: all "offered" fields are required.
 * For takeovers: "offered" fields are omitted.
 */
export const createSwapRequestSchema = type({
  // Target shift (the one the requester wants)
  planInstanceId: type('string').configure({
    description: 'ID of the plan instance containing the target shift.',
    examples: ['planinst_abc123'],
  }),
  shiftUnitId: type('string').configure({
    description: 'ID of the target shift unit the requester wants.',
    examples: ['shiftunit_abc123'],
  }),
  date: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'ISO 8601 calendar date (YYYY-MM-DD) of the target shift.',
    examples: ['2026-04-22'],
  }),
  targetEmployeeId: type('string').configure({
    description: 'ID of the employee currently assigned to the target shift.',
    examples: ['employee_abc123'],
  }),

  // Offered shift (optional — omit for takeover)
  'offeredPlanInstanceId?': type('string').configure({
    description: 'Plan instance of the offered shift (required for swap requests; omit for takeover).',
    examples: ['planinst_abc123'],
  }),
  'offeredShiftUnitId?': type('string').configure({
    description: 'Offered shift unit (required for swap requests; omit for takeover).',
    examples: ['shiftunit_def456'],
  }),
  'offeredDate?': type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    // ArkType requires literal string types; String.raw is incompatible
    description: 'ISO 8601 date (YYYY-MM-DD) of the offered shift. Required for swap requests; omit for takeover.',
    examples: ['2026-04-23'],
  }),
}).configure({ description: 'Payload for creating a swap or takeover request. Omit the offered fields to request a takeover.' });

export type CreateSwapRequest = typeof createSwapRequestSchema.t;
